/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC } from 'react';

import { I18n } from '@coze-arch/i18n';
import { IconCozBroom, IconCozEmpty } from '@coze-arch/coze-design/icons';
import { EmptyState, Search, Spin } from '@coze-arch/coze-design';

import { useAppStorePage } from './use-app-store-page';
import { ScopeDetailModal } from './scope-detail-modal';
import {
  AppStoreCard,
  copyPublishedAppLink,
  openPublishedApp,
} from './app-store-card';

import '../../theme/abr-home-theme-tech.less';
import styles from './index.module.less';

export const AppStorePage: FC = () => {
  const {
    query,
    setQuery,
    debouncedSetSearch,
    loading,
    items,
    total,
    mineCount,
    search,
    scopeModalRef,
    handleEdit,
    handleViewScope,
    handleClearSearch,
  } = useAppStorePage();

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>{I18n.t('abr_app_store_title')}</h1>
        <p className={styles.desc}>{I18n.t('abr_app_store_desc')}</p>
        <div className={styles.pills}>
          <span className={styles.pill}>
            <span className={styles.pillDot} />
            {I18n.t('abr_app_store_tenant_visible')}
          </span>
          <span className={styles.pill}>
            {I18n.t('abr_app_store_total')} <strong>{total}</strong>
          </span>
          <span className={styles.pill}>
            {I18n.t('abr_app_store_mine')} <strong>{mineCount}</strong>
          </span>
        </div>
        <div className={styles.toolbar}>
          <Search
            className={styles.search}
            placeholder={I18n.t('abr_app_store_search_placeholder')}
            value={query}
            onChange={val => {
              setQuery(String(val));
              debouncedSetSearch(String(val));
            }}
          />
          <span className={styles.count}>
            {I18n.t('abr_app_store_showing', { count: String(items.length) })}
          </span>
        </div>
      </header>

      <div className={styles.body}>
        {loading ? (
          <div className={styles.loading}>
            <Spin />
          </div>
        ) : items.length === 0 ? (
          <div className={styles.empty}>
            <EmptyState
              icon={<IconCozEmpty className="w-[48px] h-[48px] coz-fg-dim" />}
              title={
                search
                  ? I18n.t('library_empty_no_results_found_under')
                  : I18n.t('abr_app_store_empty_scope')
              }
              {...(search
                ? {
                    buttonText: I18n.t('library_empty_clear_filters'),
                    buttonProps: {
                      color: 'primary',
                      icon: <IconCozBroom />,
                    },
                    onButtonClick: handleClearSearch,
                  }
                : {})}
            />
          </div>
        ) : (
          <div className={styles.grid}>
            {items.map(item => (
              <AppStoreCard
                key={item.id}
                item={item}
                onOpen={openPublishedApp}
                onCopy={copyPublishedAppLink}
                onEdit={handleEdit}
                onViewScope={handleViewScope}
              />
            ))}
          </div>
        )}
      </div>
      <ScopeDetailModal ref={scopeModalRef} />
    </div>
  );
};
